import { useState } from 'react';
import { useUser } from '../../../../context/UserContext';
import { supabase } from '../../../../lib/supabase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faLink, faUpload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { validateFile } from './mediaUtils';
import { Button } from '../../../ui';

interface MediaHeaderProps {
  onFileUpload: () => void;
  onImageUrlClick: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  clientId?: string;
}

export default function MediaHeader({
  onFileUpload,
  onImageUrlClick,
  onError,
  onSuccess,
  clientId,
}: MediaHeaderProps) {
  const { user } = useUser();
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file || !user) return;

    if (!clientId) {
      console.error('No clientId provided for upload');
      onError('No client selected. Please refresh the page and try again.');
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      onError(validationError);
      return;
    }

    try {
      setIsUploading(true);

      // Convert file to base64 for transmission to Netlify function
      const fileReader = new FileReader();
      fileReader.onload = async () => {
        try {
          const base64Data = (fileReader.result as string).split(',')[1]; // Remove data:mime;base64, prefix

          // Get auth token
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            console.error('No authentication session found');
            throw new Error('No authentication session');
          }

          // Call Netlify function
          const response = await fetch('/.netlify/functions/r2-media-manager', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              clientId: clientId,
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              fileData: base64Data,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Netlify function error:', errorData);
            throw new Error(errorData.error || 'Upload failed');
          }

          await response.json();

          event.target.value = '';
          onFileUpload();
          onSuccess('Media file uploaded successfully!');
        } catch (err) {
          console.error('Error uploading media file:', err);
          onError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
        } finally {
          setIsUploading(false);
        }
      };

      fileReader.onerror = () => {
        onError('Failed to read file. Please try again.');
        setIsUploading(false);
      };

      fileReader.readAsDataURL(file);
    } catch (err) {
      console.error('Exception in handleFileUpload:', err);
      onError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  return (
    // wraps: the three side by side ran 90px past a phone's edge
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '8px' }}>
      {/* View Bucket on R2 */}
      {clientId && (
        <a
          href={`https://dash.cloudflare.com/d4c23ede1b944d66353daa2df1ee804b/r2/default/buckets/${clientId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="pressable hover-wash"
          title="View Bucket on R2"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            height: 'var(--control-sm)',
            padding: '0 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid transparent',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--text-sm)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '11px' }} />
          View Bucket
        </a>
      )}

      {/* Image URL Button */}
      <Button size="sm" variant="secondary" onClick={onImageUrlClick} disabled={!clientId}>
        <FontAwesomeIcon icon={faLink} style={{ fontSize: '11px', marginRight: '7px' }} />
        Image URL
      </Button>

      {/* File Upload Button */}
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <input
          type="file"
          id="media-upload"
          className="hidden"
          onChange={handleFileUpload}
          disabled={isUploading || !clientId}
          accept="image/*,video/*,audio/*,.pdf,application/pdf"
        />
        <label
          htmlFor="media-upload"
          className={`pressable ${isUploading || !clientId ? '' : 'hover-glow'}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            height: 'var(--control-sm)',
            padding: '0 10px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--accent)',
            color: 'var(--text-on-accent)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
            cursor: isUploading || !clientId ? 'not-allowed' : 'pointer',
            opacity: isUploading || !clientId ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          <FontAwesomeIcon
            icon={isUploading ? faSpinner : faUpload}
            className={isUploading ? 'animate-spin' : undefined}
            style={{ fontSize: '11px' }}
          />
          {isUploading ? 'Uploading...' : 'Upload Media'}
        </label>
      </span>
    </div>
  );
}
